import React, { useState } from "react";
import { ChevronDown, ChevronRight, Copy, Eye, EyeOff, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PdLoading } from "@/components/PdPill";

const copy = (text, label) =>
  navigator.clipboard.writeText(text).then(() => toast.success(`Copied ${label}`)).catch(() => toast.error("Copy failed"));

function LoginFields({ login, isAdmin, testid }) {
  const [show, setShow] = useState(false);
  if (!login?.username || !login?.password) {
    return (
      <span className="text-[11px] text-[#C8553D]" data-testid={`${testid}-missing`}>
        {isAdmin ? "Login save nahi hai — Delivery Logins page se save karo" : "Login save nahi hai — Admin se contact karo"}
      </span>
    );
  }
  const btn = "p-1 rounded hover:bg-[#EFEEE6] text-[#6B6B61]";
  return (
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs font-mono">
      <span className="flex items-center gap-1">
        <span className="text-[#6B6B61]">ID:</span><b data-testid={`${testid}-user`}>{login.username}</b>
        <button className={btn} onClick={() => copy(login.username, "User ID")} data-testid={`${testid}-copy-user`}><Copy className="w-3 h-3" /></button>
      </span>
      <span className="flex items-center gap-1">
        <span className="text-[#6B6B61]">Pass:</span><b data-testid={`${testid}-pass`}>{show ? login.password : "••••••"}</b>
        <button className={btn} onClick={() => setShow((v) => !v)} data-testid={`${testid}-toggle-pass`}>
          {show ? <EyeOff className="w-3 h-3" /> : <Eye className="w-3 h-3" />}
        </button>
        <button className={btn} onClick={() => copy(login.password, "Password")} data-testid={`${testid}-copy-pass`}><Copy className="w-3 h-3" /></button>
      </span>
    </div>
  );
}

const bigBtn = "grid place-items-center w-12 h-12 shrink-0 rounded-xl border border-[#E0DFD6] bg-white text-[#385C4B] hover:bg-[#F4F3EC] active:scale-95 transition-[transform,background-color]";

function BigField({ label, value, secret, testid }) {
  const [show, setShow] = useState(false);
  return (
    <div className="flex items-center gap-2">
      <div className="min-w-0 flex-1 rounded-xl bg-[#F4F3EC] px-3.5 py-2">
        <div className="text-[10px] uppercase tracking-[0.16em] text-[#6B6B61] font-semibold">{label}</div>
        <div className="font-mono text-base font-bold text-[#1A1A1A] truncate" data-testid={`${testid}-value`}>{secret && !show ? "••••••••" : value}</div>
      </div>
      {secret && (
        <button onClick={() => setShow((v) => !v)} className={bigBtn} data-testid={`${testid}-toggle`} aria-label="Show password">
          {show ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
        </button>
      )}
      <button onClick={() => copy(value, label)} className={bigBtn} data-testid={`${testid}-copy`} aria-label={`Copy ${label}`}><Copy className="w-5 h-5" /></button>
    </div>
  );
}

export function LoginsScreen({ logins }) {
  if (!logins) return <PdLoading />;
  if (!logins.length) {
    return <div className="rounded-2xl border border-dashed border-[#DCDCD3] bg-white/60 p-6 text-sm text-center text-[#6B6B61]" data-testid="dm-logins-empty">Koi agency assign nahi hui.</div>;
  }
  return (
    <div className="space-y-3 animate-in fade-in duration-300" data-testid="dm-logins">
      <p className="text-sm text-[#6B6B61]">Portal login ke liye User ID / Password copy karo.</p>
      {logins.map((l) => (
        <div key={l.group_id} className="bg-white rounded-2xl border border-[#E5E5DF] p-4 shadow-sm space-y-2.5" data-testid={`dm-login-card-${l.group_id}`}>
          <div className="flex items-center gap-2.5 mb-1">
            <span className="grid place-items-center w-9 h-9 rounded-full bg-[#8A6046]/10"><KeyRound className="w-4 h-4 text-[#8A6046]" /></span>
            <span className="font-semibold text-sm text-[#1A1A1A] truncate">{l.name}</span>
          </div>
          {l.username && l.password ? (
            <>
              <BigField label="User ID" value={l.username} testid={`dm-login-${l.group_id}-user`} />
              <BigField label="Password" value={l.password} secret testid={`dm-login-${l.group_id}-pass`} />
            </>
          ) : (
            <div className="text-sm text-[#C8553D] rounded-xl bg-[#FDECEA] px-3 py-2.5" data-testid={`dm-login-${l.group_id}-missing`}>Login save nahi hai — Admin se contact karo</div>
          )}
        </div>
      ))}
    </div>
  );
}

export function AgencyLoginsCard({ logins, isAdmin }) {
  const [open, setOpen] = useState(!isAdmin);
  if (!logins?.length) return null;
  const saved = logins.filter((l) => l.username && l.password).length;
  return (
    <div className="bg-white border border-[#E5E5DF] rounded-xl shadow-sm overflow-hidden" data-testid="pd-logins-card">
      <button onClick={() => setOpen((v) => !v)} className="w-full flex items-center gap-2 px-3 py-2.5 hover:bg-[#F8F8F3]" data-testid="pd-logins-toggle">
        {open ? <ChevronDown className="w-4 h-4 text-[#385C4B]" /> : <ChevronRight className="w-4 h-4 text-[#385C4B]" />}
        <KeyRound className="w-4 h-4 text-[#8A6046]" />
        <span className="font-bold text-sm">{isAdmin ? "Agency Logins" : "Meri Agency Logins"} ({logins.length})</span>
        <span className="ml-auto text-[10px] font-mono text-[#6B6B61]" data-testid="pd-logins-saved">{saved} saved</span>
      </button>
      {open && (
        <div className="divide-y divide-[#F0F0EA] border-t border-[#EDEDE5]">
          {logins.map((l) => (
            <div key={l.group_id} className="px-3 py-2" data-testid={`pd-login-row-${l.group_id}`}>
              <div className="text-xs font-bold truncate mb-0.5">{l.name}</div>
              <LoginFields login={l} isAdmin={isAdmin} testid={`pd-login-${l.group_id}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function AgencyLoginButton({ a, isAdmin }) {
  return (
    <span onClick={(e) => e.stopPropagation()}>
      <Popover>
        <PopoverTrigger asChild>
          <button data-testid={`pd-login-btn-${a.group_id}`}
            className={`h-8 text-[11px] font-semibold uppercase tracking-wide px-2.5 rounded-lg border flex items-center gap-1 bg-white transition-colors hover:bg-[#F4F3EC] ${a.login ? "border-[#8A6046]/60 text-[#8A6046]" : "border-[#E0DFD6] text-[#8A8A80]"}`}>
            <KeyRound className="w-3 h-3" />Login
          </button>
        </PopoverTrigger>
        <PopoverContent align="end" className="w-auto max-w-[320px] p-3" data-testid={`pd-login-pop-${a.group_id}`}>
          <div className="text-xs font-bold mb-1.5 truncate">{a.name}</div>
          <LoginFields login={a.login} isAdmin={isAdmin} testid={`pd-login-pop-f-${a.group_id}`} />
        </PopoverContent>
      </Popover>
    </span>
  );
}
